import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import config from "@/config";
import { Themes } from "@/types/theme";
import { stringToEnum } from "@/utils/technical.utils";
import { useKypay } from "../kypay/hooks/use-kypay";
import { MonextStatus } from "./types";

type MonextWidgetProps = {
  sessionId: string;
  referenceId: string;
  onPaymentComplete: (status: MonextStatus) => void | Promise<void>;
};

export const MonextWidget = ({ sessionId, referenceId, onPaymentComplete }: MonextWidgetProps) => {
  const { t } = useTranslation(["pass", "error"]);
  const [transactionStatus, setTransactionStatus] = useState<MonextStatus>(MonextStatus.PENDING);
  const [waitingWebhook, setWaitingWebhook] = useState<boolean>(false);
  const { getTransactionReferenceMutation } = useKypay();
  const { mutateAsync: getTransactionReference } = getTransactionReferenceMutation();

  const paylineWidgetRef = useRef<HTMLIFrameElement>(null);
  const pollingIntervalIdRef = useRef<NodeJS.Timeout>(null);
  const statusIsLoading = useRef<boolean>(false);

  useEffect(() => {
    injectScript();

    if (!paylineWidgetRef.current) return;

    const observerCallback = () => {
      if (!paylineWidgetRef.current) return;

      const innerText = paylineWidgetRef.current.innerText;
      if (innerText.includes("Référence") || innerText.includes("Reference")) {
        setWaitingWebhook(true);
        pollTransactionStatus();
        observer.disconnect();
      } else if (innerText.includes("Paiement refusé") || innerText.includes("Payment declined")) {
        onPaymentComplete(MonextStatus.FAILED);
        observer.disconnect();
      }
    };

    const observer = new MutationObserver(observerCallback);
    const config = { attributes: true, childList: true, subtree: true };

    observer.observe(paylineWidgetRef.current, config);
    return () => {
      observer.disconnect();
    };
  }, []);

  const injectScript = () => {
    const script = document.createElement("script");
    script.src = config.PAYLINE_JS_LINK;
    script.async = true;
    document.body.appendChild(script);
  };

  const clearPollingInterval = () => {
    if (!pollingIntervalIdRef.current) return;

    clearInterval(pollingIntervalIdRef.current);
  };

  const pollTransactionStatus = async () => {
    // Max 2 minutes
    let attempts = 0;
    const maxAttempts = 80;

    pollingIntervalIdRef.current = setInterval(async () => {
      try {
        const status = await getTransactionStatus();

        if (status !== MonextStatus.PENDING) {
          setTransactionStatus(status);
          if (statusIsLoading.current) {
            onPaymentComplete(status);
          }
          clearPollingInterval();
        }
      } catch (_) {
      } finally {
        attempts++;
        if (attempts >= maxAttempts) {
          clearPollingInterval();
          toast.error(t("bitbang.internal_code.default"));
          onPaymentComplete(MonextStatus.INTERNAL_ERROR);
        }
      }
    }, 1500);
  };

  const getTransactionStatus = async (): Promise<MonextStatus> => {
    const { status } = await getTransactionReference({
      transactionReference: referenceId
    });

    return stringToEnum(status, MonextStatus, MonextStatus.INTERNAL_ERROR);
  };

  const handleConfirm = async () => {
    statusIsLoading.current = true;

    if (transactionStatus === MonextStatus.PENDING) {
      return;
    }

    clearPollingInterval();
    onPaymentComplete(transactionStatus);
  };

  return (
    <div>
      <link href={config.PAYLINE_CSS_LINK} rel="stylesheet" />

      <div
        ref={paylineWidgetRef}
        id="PaylineWidget"
        data-token={sessionId}
        data-template="column"
        data-embeddedredirectionallowed="true"
      />

      {waitingWebhook && (
        <div className="flex flex-row justify-center">
          <Button
            className={"mt-6 w-auto"}
            loading={statusIsLoading.current}
            theme={Themes.primary}
            onClick={() => {
              handleConfirm();
            }}
          >
            {t("pass:olkypass.general.finalise")}
          </Button>
        </div>
      )}
    </div>
  );
};
